import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, ArrowUpRight } from "lucide-react";
import { IMAGES, pimg } from "../lib/site";

const EASE_SLIDE = [0.76, 0, 0.24, 1];

const HeroBannerSlider = ({ banners = [] }) => {
  const slides = Array.isArray(banners) ? banners : [];
  const [current, setCurrent] = useState(0);
  const [direction, setDirection] = useState(1);
  const AUTO_DELAY = 6000;

  const goTo = useCallback(
    (idx, dir = 1) => {
      if (slides.length === 0) return;
      setDirection(dir);
      setCurrent((idx + slides.length) % slides.length);
    },
    [slides.length]
  );

  const next = useCallback(() => goTo(current + 1, 1), [current, goTo]);
  const prev = useCallback(() => goTo(current - 1, -1), [current, goTo]);

  // Auto-advance
  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setTimeout(next, AUTO_DELAY);
    return () => clearTimeout(timer);
  }, [current, next, slides.length]);

  // Keyboard navigation
  useEffect(() => {
    if (slides.length <= 1) return;
    const onKey = (e) => {
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [next, prev, slides.length]);

  // Resolve image URL
  const resolveImage = (s) => {
    if (!s) return "";
    if (s.image_path && (s.image_path.startsWith("http://") || s.image_path.startsWith("https://"))) return s.image_path;
    if (s.image_path) {
      const clean = s.image_path.startsWith("/") ? s.image_path.substring(1) : s.image_path;
      return pimg(`storage/${clean}`);
    }
    if (s.image_url) return s.image_url;
    return "";
  };

  if (slides.length === 0) {
    return (
      <section
        className="relative overflow-hidden bg-brand-ink"
        style={{ minHeight: "92vh" }}
        data-testid="hero-slider"
      >
        <div className="blueprint-grid-dark pointer-events-none absolute inset-0 opacity-20" aria-hidden="true" />
        <div className="relative z-10 mx-auto flex h-full min-h-[92vh] max-w-[1400px] flex-col justify-center px-5 py-20 lg:px-10">
          <div className="max-w-3xl animate-pulse">
            <div className="h-4 w-48 bg-white/20 mb-6 rounded" />
            <div className="h-16 w-3/4 bg-white/20 mb-6 rounded" />
            <div className="h-6 w-1/2 bg-white/10 mb-8 rounded" />
            <div className="flex gap-4">
              <div className="h-12 w-36 bg-brand-red/60 rounded" />
              <div className="h-12 w-36 bg-white/20 rounded" />
            </div>
          </div>
        </div>
      </section>
    );
  }

  const slide = slides[current] || slides[0];
  const opacity = Math.min(90, Math.max(0, Number(slide?.overlay_opacity ?? 60))) / 100;

  const variants = {
    enter: (dir) => ({ opacity: 0, x: dir > 0 ? 60 : -60, scale: 1.02 }),
    center: { opacity: 1, x: 0, scale: 1 },
    exit: (dir) => ({ opacity: 0, x: dir > 0 ? -60 : 60, scale: 0.98 }),
  };

  return (
    <section
      className="relative overflow-hidden bg-brand-ink"
      style={{ minHeight: "92vh" }}
      data-testid="hero-slider"
      aria-label="Hero banner slideshow"
    >
      {/* Slides */}
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={current}
          custom={direction}
          variants={variants}
          initial="enter"
          animate="center"
          exit="exit"
          transition={{ duration: 0.75, ease: EASE_SLIDE }}
          className="absolute inset-0"
          aria-hidden="false"
        >
          {/* Background Image */}
          <img
            src={resolveImage(slide)}
            alt={slide.title}
            className="absolute inset-0 h-full w-full object-cover"
            loading="eager"
            fetchPriority={current === 0 ? "high" : "auto"}
          />

          {/* Dark Overlay */}
          <div
            className="absolute inset-0 bg-brand-ink"
            style={{ opacity }}
            aria-hidden="true"
          />

          {/* Blueprint grid texture */}
          <div className="blueprint-grid-dark pointer-events-none absolute inset-0 opacity-20" aria-hidden="true" />
        </motion.div>
      </AnimatePresence>

      {/* Content */}
      <div className="relative z-10 mx-auto flex h-full min-h-[92vh] max-w-[1400px] flex-col justify-center px-5 py-20 lg:px-10">
        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={`content-${current}`}
            custom={direction}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.6, ease: EASE_SLIDE, delay: 0.15 }}
            className="max-w-3xl"
          >
            {/* Overline badge */}
            {slide.subtitle && (
              <p className="mono-caption mb-6 flex items-center gap-3 text-white/60">
                <span className="inline-block h-2 w-2 shrink-0 bg-brand-red" aria-hidden="true" />
                {slide.subtitle}
              </p>
            )}

            {/* Main headline */}
            <h1
              className="font-display text-5xl font-semibold leading-[0.93] tracking-tighter text-white sm:text-6xl lg:text-7xl xl:text-8xl"
              data-testid="hero-heading"
            >
              {slide.title}
            </h1>

            {/* Subheading */}
            {slide.description && (
              <p className="mt-7 max-w-xl text-base leading-relaxed text-white/70 md:text-lg">
                {slide.description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="mt-10 flex flex-wrap gap-4">
              {slide.cta_text && slide.cta_url && (
                <Link
                  to={slide.cta_url}
                  className="btn-primary"
                  data-testid="hero-cta-primary"
                >
                  {slide.cta_text} <ArrowUpRight size={16} />
                </Link>
              )}
              {slide.cta2_text && slide.cta2_url && (
                <Link
                  to={slide.cta2_url}
                  className="btn-outline-white"
                  data-testid="hero-cta-secondary"
                >
                  {slide.cta2_text}
                </Link>
              )}
            </div>

            {/* Stats strip */}
            <div className="mt-14 flex flex-wrap gap-x-10 gap-y-4 border-t border-white/15 pt-6">
              {[["40+", "Countries Served"], ["1M+", "Units Shipped"], ["12", "Years Manufacturing"]].map(
                ([n, l]) => (
                  <div key={l}>
                    <p className="font-display text-2xl font-semibold text-white">{n}</p>
                    <p className="mono-caption mt-1 text-white/40">{l}</p>
                  </div>
                )
              )}
            </div>
          </motion.div>
        </AnimatePresence>

        {/* Slide controls row — bottom */}
        <div className="absolute bottom-8 left-5 right-5 z-20 flex items-center justify-between lg:left-10 lg:right-10">
          {/* Dot indicators */}
          <div className="flex items-center gap-2" role="tablist" aria-label="Slide indicators">
            {slides.map((_, i) => (
              <button
                key={i}
                role="tab"
                aria-selected={i === current}
                aria-label={`Go to slide ${i + 1}`}
                data-testid={`hero-dot-${i}`}
                onClick={() => goTo(i, i > current ? 1 : -1)}
                className={`h-1 rounded-full transition-all duration-500 ${
                  i === current ? "w-8 bg-brand-red" : "w-3 bg-white/30 hover:bg-white/60"
                }`}
              />
            ))}
          </div>

          {/* Arrow controls */}
          <div className="flex items-center gap-2">
            <button
              onClick={prev}
              aria-label="Previous slide"
              data-testid="hero-prev"
              className="flex h-10 w-10 items-center justify-center border border-white/20 text-white/70 backdrop-blur-sm transition-colors hover:border-brand-red hover:bg-brand-red hover:text-white"
            >
              <ChevronLeft size={20} />
            </button>
            <button
              onClick={next}
              aria-label="Next slide"
              data-testid="hero-next"
              className="flex h-10 w-10 items-center justify-center border border-white/20 text-white/70 backdrop-blur-sm transition-colors hover:border-brand-red hover:bg-brand-red hover:text-white"
            >
              <ChevronRight size={20} />
            </button>

            {/* Slide counter */}
            <span className="ml-2 font-mono text-[11px] uppercase tracking-[0.2em] text-white/40">
              {String(current + 1).padStart(2, "0")} / {String(slides.length).padStart(2, "0")}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroBannerSlider;
